import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PulpoSnackbarComponent } from '../components/pulpo-snackbar/pulpo-snackbar.component';
import { TranslocoService } from '@jsverse/transloco';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy, SILENT_ERROR } from '@pulpo/pulpo-api';

export interface NormalizedError {
  code?: string;
  message?: string;
}

export function normalizeBackendError(errorBody: any): NormalizedError {
  if (!errorBody) return {};

  // HttpError (legacy): { key, message, status }
  if (errorBody.key) {
    return { code: errorBody.key, message: errorBody.message };
  }

  // ApiError (new): { error, debugMessage, status }
  if (errorBody.error && errorBody.debugMessage) {
    return { code: errorBody.error, message: errorBody.debugMessage };
  }

  // PulpoError (new): { error, message }
  if (errorBody.error) {
    return { code: errorBody.error, message: errorBody.message };
  }

  // ErrorDTO (legacy): { message, status }
  if (errorBody.message) {
    return { message: errorBody.message };
  }

  return {};
}

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private snackBar = inject(MatSnackBar);
  private translocoService = inject(TranslocoService);
  private routeReuseStrategy = inject(RouteReuseStrategy);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error &&
          error.status !== 404 &&
          !request.context.get(SILENT_ERROR) &&
          !request.url.endsWith('/role') &&
          !request.url.endsWith('/public/users/account')
        ) {
          (
            this.routeReuseStrategy as CustomReuseStrategy
          ).clearAllSavedHandles();

          // Les requêtes en responseType 'blob' (exports xlsx, pdf...) reçoivent
          // le corps d'erreur sous forme de Blob : il faut le relire pour
          // retrouver le message du back.
          if (error.error instanceof Blob) {
            from(error.error.text()).subscribe((text) => {
              this.displayError(normalizeBackendError(this.parseJson(text)));
            });
          } else {
            this.displayError(normalizeBackendError(error.error));
          }
        }
        return throwError(() => {
          return error;
        });
      })
    );
  }

  private parseJson(text: string): any {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  private displayError(normalized: NormalizedError): void {
    const displayMessage =
      normalized.message || 'Une erreur inconue est survenue';

    if (normalized.code) {
      this.translocoService
        .selectTranslate(normalized.code)
        .subscribe((res) => {
          this.showSnackbar(res || displayMessage);
        });
    } else {
      this.showSnackbar(displayMessage);
    }
  }

  private showSnackbar(message: string): void {
    this.snackBar.openFromComponent(PulpoSnackbarComponent, {
      data: {
        title: 'Une erreur est survenue !',
        message,
        backgroundColor: '#ec2b2b',
        accentColor: 'white',
      },
      duration: 3000,
      panelClass: ['snackbar'],
    });
  }
}
